import { Component, type SyntheticEvent } from "react";
import { pick } from "ramda";
import { observer } from "mobx-react";
import { Stack, TextField, DefaultButton } from "@fluentui/react";
import { action, computed, makeObservable } from "mobx";
import { type ShiftUpdate, type Shift } from "../../api";
import { doubleBindString } from "../../utils/double-bind";
import { createUuid } from "../../utils/uuid";
import { type FormUpdateProps } from "../../utils/form-update-props";
import { PrimaryButtonValidation } from "../atoms/primary-button-validation";
import { ElofleetDialogFooter } from "../atoms/elofleet-dialog-footer";
import sizes from "../sizes.scss";

export interface FormUpdateShiftProps extends FormUpdateProps<Shift> {}

import { store } from "../../store";

@observer
export class FormUpdateShift extends Component<FormUpdateShiftProps> {
    private validationId = createUuid();

    constructor(props: FormUpdateShiftProps) {
        super(props);
        makeObservable(this);
    }

    private triggerValidation(): void {
        store.repositoryShifts.validation.update.updateModel(this.validationId, this.shiftUpdate);
    }

    componentDidMount(): void {
        store.repositoryShifts.validation.update.initializeModel(
            this.validationId,
            this.shiftUpdate,
            this.props.id,
        );
    }

    public componentWillUnmount(): void {
        store.repositoryShifts.discardMutableCopy(FormUpdateShift.name, this.props.id);
    }

    @computed private get shift(): Shift | undefined {
        return store.repositoryShifts.mutableCopyById(FormUpdateShift.name, this.props.id);
    }

    @computed private get shiftUpdate(): ShiftUpdate {
        return pick(["label"], this.shift);
    }

    @action.bound
    private async updateShift(evt: SyntheticEvent<HTMLFormElement>): Promise<void> {
        evt.preventDefault();
        const shift = await store.repositoryShifts.update(this.props.id, this.shiftUpdate);
        if (this.props.onUpdate) {
            this.props.onUpdate(shift);
        }
    }

    public render(): JSX.Element {
        const primaryButton = (
            <PrimaryButtonValidation
                text={store.i18n.t("formUpdateShift.submit.text")}
                validation={store.repositoryShifts.validation.update}
                validationId={this.validationId}
            />
        );
        return (
            <form onSubmit={this.updateShift}>
                <Stack tokens={{ padding: `0px ${sizes.formPaddingHorizontal}` }}>
                    <TextField
                        label={store.i18n.t("formUpdateShift.label.label")}
                        disabled={!this.shift}
                        {...doubleBindString(this.shift!, "label", () => this.triggerValidation())}
                        required
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryShifts.validation.update.getFieldValidationState(
                                this.validationId,
                                "label",
                            ),
                        )}
                    />
                </Stack>
                {this.props.asDialogContent ? (
                    <ElofleetDialogFooter>
                        <DefaultButton
                            text={store.i18n.t("formUpdateShift.cancel.text")}
                            onClick={this.props.onDialogCancel}
                        />
                        {primaryButton}
                    </ElofleetDialogFooter>
                ) : (
                    <Stack horizontal horizontalAlign="end">
                        {primaryButton}
                    </Stack>
                )}
            </form>
        );
    }
}
